import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MapPin, Sparkles, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import DashboardLayout from "@/layouts/DashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { speaksSpanish, setSpanish } from "@/components/marketplace/SpanishBadge";

const STEPS = [
  { key: "type", label: "Type" },
  { key: "identity", label: "Identity" },
  { key: "services", label: "Services" },
  { key: "location", label: "Location" },
  { key: "review", label: "Review" },
];

export default function ProviderOnboardingPage() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    display_name: user?.display_name || "",
    business_name: "",
    provider_type: "local",
    provider_variant: "individual",
    tagline: "",
    short_intro: "",
    full_bio: "",
    years_experience: 1,
    website: "",
    category_ids: [],
    skills: "",
    location_ids: [],
    primary_location_id: "",
    service_radius_miles: 25,
    remote_available: false,
    profile_image: user?.picture || "",
    languages: ["English"],
  });

  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityQ, setCityQ] = useState("");
  const [selectedState, setSelectedState] = useState("");

  // ---- Bootstrapping ---------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [me, cats, sts] = await Promise.all([
          api.get("/providers/me/profile"),
          api.get("/categories"),
          api.get("/locations/states"),
        ]);
        setCategories(cats.data);
        setStates(sts.data);
        if (me.data) {
          setProfile(me.data);
          setForm((f) => ({
            ...f,
            ...me.data,
            skills: (me.data.skills || []).join(", "),
            category_ids: me.data.category_ids || [],
            location_ids: me.data.location_ids || [],
          }));
          const stateFromId = sts.data.find((s) => (me.data.location_ids || []).includes(s.id));
          if (stateFromId) setSelectedState(stateFromId.state_code);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedState) { setCities([]); return; }
    (async () => {
      const { data } = await api.get("/locations/cities", { params: { state_code: selectedState, q: cityQ || undefined, limit: 3000 } });
      setCities(data);
    })();
  }, [selectedState, cityQ]);

  // ---- Actions --------------------------------------------------------

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggle = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const createDraft = async () => {
    if (profile) return profile;
    try {
      const { data } = await api.post("/providers/me/profile", {
        display_name: form.display_name || user.display_name,
        provider_type: form.provider_type,
        provider_variant: form.provider_variant,
        business_name: form.business_name || null,
        tagline: form.tagline || null,
        short_intro: form.short_intro || null,
      });
      setProfile(data);
      await refresh();
      toast.success("Draft profile created.");
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create draft");
      throw err;
    }
  };

  const saveDraft = async (goNext = true) => {
    try {
      if (!profile) await createDraft();
      const payload = {
        display_name: form.display_name,
        business_name: form.business_name || null,
        tagline: form.tagline || null,
        short_intro: form.short_intro || null,
        full_bio: form.full_bio || null,
        years_experience: Number(form.years_experience) || 0,
        website: form.website || null,
        category_ids: form.category_ids,
        skills: (form.skills || "").split(",").map((s) => s.trim()).filter(Boolean),
        location_ids: form.location_ids,
        primary_location_id: form.primary_location_id || null,
        service_radius_miles: form.provider_type === "digital" ? null : Number(form.service_radius_miles) || null,
        remote_available: !!form.remote_available,
        profile_image: form.profile_image || null,
        languages: form.languages?.length ? form.languages : ["English"],
      };
      const { data } = await api.patch("/providers/me/profile", payload);
      setProfile(data);
      toast.success("Progress saved.");
      if (goNext) setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save");
    }
  };

  const submit = async () => {
    try {
      await saveDraft(false);
      const { data } = await api.post("/providers/me/profile/submit");
      setProfile(data);
      toast.success("Submitted for review — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not submit");
    }
  };

  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-[#252A34]/60">Loading your provider profile…</div>
      </DashboardLayout>
    );
  }

  const isSubmitted = profile?.status && profile.status !== "draft";

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Become a provider</div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22] tracking-tight" data-testid="onboarding-title">
          Set up your Gulf Coast Work profile.
        </h1>
        <p className="text-[#252A34]/70 mt-1 text-sm max-w-2xl">
          Draft-first — fill in as much as you like, we'll auto-save so you can come back and finish anytime. When it's ready, hit submit for GCW review.
        </p>
      </div>

      {isSubmitted ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 flex items-start gap-4 max-w-3xl" data-testid="onboarding-submitted">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <div>
            <div className="font-display text-lg font-semibold text-emerald-900">
              Your profile is <span className="capitalize">{profile.status}</span>
            </div>
            <div className="text-sm text-emerald-900/80 mt-1">
              You can keep editing while we review — see your public page{" "}
              <a href={`/providers/${profile.slug}`} className="underline">here</a>.
            </div>
          </div>
        </div>
      ) : null}

      {/* Step nav */}
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-center gap-4" data-testid="onboarding-steps">
        <div className="flex items-center gap-1 flex-1 overflow-x-auto">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`step-${s.key}`}
              className={`px-3 py-2 text-sm rounded-full font-medium whitespace-nowrap transition-colors ${
                step === i ? "bg-[#071b22] text-white" : i < step ? "text-[#165a71] bg-[#165a71]/10" : "text-[#252A34]/60"
              }`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-[#252A34]/60 shrink-0">{progress}% complete</div>
      </div>

      <motion.div
        key={step}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
      >
        {step === 0 && (
          <StepShell title="What kind of provider are you?" subtitle="You can offer both local and digital services — pick what fits best today.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="step-type-options">
              {[
                { key: "local", label: "Local services", desc: "In-person work — cleaning, handyman, movers, lawn care." },
                { key: "digital", label: "Digital talent", desc: "Design, dev, marketing, video, translations." },
                { key: "hybrid", label: "Hybrid", desc: "Mix of on-site and online (photography, tutoring, events)." },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => upd({ provider_type: t.key })}
                  data-testid={`type-${t.key}`}
                  className={`text-left rounded-2xl border p-5 hover:border-[#165a71]/40 transition-colors ${
                    form.provider_type === t.key ? "border-[#165a71] ring-2 ring-[#165a71]/20 bg-[#165a71]/5" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="font-display text-lg font-semibold text-[#071b22]">{t.label}</div>
                  <div className="text-sm text-[#252A34]/70 mt-1">{t.desc}</div>
                </button>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "individual", label: "Individual", desc: "It's just me." },
                { key: "business", label: "Business", desc: "Registered business, might have a small team." },
                { key: "agency", label: "Agency", desc: "Multiple providers under one brand." },
              ].map((v) => (
                <button
                  key={v.key}
                  onClick={() => upd({ provider_variant: v.key })}
                  data-testid={`variant-${v.key}`}
                  className={`text-left rounded-2xl border p-4 hover:border-[#165a71]/40 transition-colors ${
                    form.provider_variant === v.key ? "border-[#165a71] ring-2 ring-[#165a71]/20 bg-[#165a71]/5" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="font-display font-semibold text-[#071b22]">{v.label}</div>
                  <div className="text-xs text-[#252A34]/70 mt-0.5">{v.desc}</div>
                </button>
              ))}
            </div>
          </StepShell>
        )}

        {step === 1 && (
          <StepShell title="Tell us who you are" subtitle="This is what customers see first.">
            <div className="grid gap-5 max-w-xl">
              <Field label="Display name">
                <input value={form.display_name} onChange={(e) => upd({ display_name: e.target.value })} className="input" data-testid="onb-display-name" />
              </Field>
              {form.provider_variant !== "individual" && (
                <Field label="Business / agency name">
                  <input value={form.business_name} onChange={(e) => upd({ business_name: e.target.value })} className="input" data-testid="onb-business-name" />
                </Field>
              )}
              <Field label="Tagline">
                <input value={form.tagline} onChange={(e) => upd({ tagline: e.target.value })} className="input" placeholder="e.g. Detail-obsessed cleaner serving Pensacola" data-testid="onb-tagline" />
              </Field>
              <Field label="Short intro">
                <textarea rows={2} value={form.short_intro} onChange={(e) => upd({ short_intro: e.target.value })} className="input" placeholder="One or two sentences — first impression counts." data-testid="onb-short-intro" />
              </Field>
              <Field label="Full bio">
                <textarea rows={5} value={form.full_bio} onChange={(e) => upd({ full_bio: e.target.value })} className="input" placeholder="Your story: experience, approach, what makes you different." data-testid="onb-full-bio" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Years experience">
                  <input type="number" min="0" value={form.years_experience} onChange={(e) => upd({ years_experience: e.target.value })} className="input" data-testid="onb-years" />
                </Field>
                <Field label="Website (optional)">
                  <input value={form.website} onChange={(e) => upd({ website: e.target.value })} className="input" placeholder="https://…" data-testid="onb-website" />
                </Field>
              </div>
              <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-[#165a71]/40 transition-colors" data-testid="onb-speaks-spanish">
                <input type="checkbox" checked={speaksSpanish(form.languages)} onChange={(e) => upd({ languages: setSpanish(form.languages, e.target.checked) })} className="mt-1 h-4 w-4 accent-[#165a71]" data-testid="onb-speaks-spanish-input" />
                <span>
                  <span className="block text-sm font-semibold text-[#071b22]">I speak Spanish</span>
                  <span className="block text-xs text-[#252A34]/60">Shows a <span translate="no" className="font-semibold">"Se habla español"</span> badge on your public profile.</span>
                </span>
              </label>
              <Field label="Profile picture URL (optional)">
                <input value={form.profile_image} onChange={(e) => upd({ profile_image: e.target.value })} className="input" placeholder="https://…" data-testid="onb-picture" />
              </Field>
            </div>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell title="What do you offer?" subtitle={`Pick up to 5 categories that match your ${form.provider_type} services.`}>
            <div className="mb-4 flex items-center gap-2 text-sm">
              <Badge variant="outline" className="rounded-full">{form.category_ids.length} selected</Badge>
              {form.category_ids.length > 5 && <span className="text-red-600 text-xs">Pick at most 5.</span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-80 overflow-y-auto pr-1" data-testid="category-picker">
              {categories
                .filter((c) => (form.provider_type === "hybrid" ? true : c.kind === form.provider_type))
                .filter((c) => c.depth === 0)
                .map((c) => {
                  const on = form.category_ids.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => upd({ category_ids: toggle(form.category_ids, c.id) })}
                      data-testid={`pick-category-${c.slug}`}
                      className={`text-left rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                        on ? "border-[#165a71] bg-[#165a71]/5 text-[#071b22]" : "border-slate-200 text-[#252A34]/80 hover:border-[#165a71]/40"
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
            </div>
            <div className="mt-6 max-w-xl">
              <Field label="Skills / keywords (comma-separated)">
                <input value={form.skills} onChange={(e) => upd({ skills: e.target.value })} className="input" placeholder="detail-oriented, insured, eco-friendly, licensed" data-testid="onb-skills" />
              </Field>
            </div>
          </StepShell>
        )}

        {step === 3 && (
          <StepShell title="Where do you serve?" subtitle="Choose your primary state and cities — you can adjust radius or add remote.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <Field label="Primary state">
                <select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} className="input" data-testid="onb-state">
                  <option value="">Select…</option>
                  {states.map((s) => <option key={s.id} value={s.state_code}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Service radius (miles)">
                <input type="number" min="0" max="500" value={form.service_radius_miles} onChange={(e) => upd({ service_radius_miles: e.target.value })} className="input" disabled={form.provider_type === "digital"} data-testid="onb-radius" />
              </Field>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="text-sm font-medium text-[#071b22]">Cities you serve {form.location_ids.length > 0 && <span className="text-[#165a71]">· {form.location_ids.length} selected</span>}</div>
                <input value={cityQ} onChange={(e) => setCityQ(e.target.value)} placeholder="Search cities…" className="input max-w-[220px]" disabled={!selectedState} data-testid="onb-city-search" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="onb-city-picker">
                {cities.length === 0 && <div className="text-sm text-[#252A34]/60 col-span-full">Pick a state to see cities.</div>}
                {cities.map((c) => {
                  const on = form.location_ids.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => {
                        const next = toggle(form.location_ids, c.id);
                        upd({ location_ids: next, primary_location_id: form.primary_location_id || (next[0] || "") });
                      }}
                      data-testid={`pick-city-${c.slug}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        on ? "border-[#165a71] bg-[#165a71]/10 text-[#165a71]" : "border-slate-200 text-[#252A34]/70 hover:border-[#165a71]/40"
                      }`}
                    >
                      <MapPin className="h-3.5 w-3.5" /> {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="mt-6 inline-flex items-center gap-2 text-sm text-[#252A34]">
              <input type="checkbox" checked={form.remote_available} onChange={(e) => upd({ remote_available: e.target.checked })} data-testid="onb-remote" className="h-4 w-4 rounded border-slate-300 text-[#165a71] focus:ring-[#165a71]" />
              I'm available for remote work
            </label>
          </StepShell>
        )}

        {step === 4 && (
          <StepShell title="Review & submit" subtitle="Double-check what customers will see — you can keep editing after review.">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-5">
                <div className="text-xs uppercase tracking-wider text-[#252A34]/60 font-medium">Identity</div>
                <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">{form.business_name || form.display_name}</div>
                <div className="text-sm text-[#252A34]/70 mt-1">{form.tagline}</div>
                <p className="text-sm text-[#252A34]/85 mt-3 whitespace-pre-line">{form.short_intro}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-5 space-y-2 text-sm">
                <div><span className="text-[#252A34]/60">Type:</span> <span className="capitalize font-medium">{form.provider_type} / {form.provider_variant}</span></div>
                <div><span className="text-[#252A34]/60">Categories:</span> {form.category_ids.length}</div>
                <div><span className="text-[#252A34]/60">Locations:</span> {form.location_ids.length}</div>
                <div><span className="text-[#252A34]/60">Remote:</span> {form.remote_available ? "Yes" : "No"}</div>
                <div><span className="text-[#252A34]/60">Radius:</span> {form.provider_type === "digital" ? "n/a" : `${form.service_radius_miles} mi`}</div>
              </div>
            </div>
          </StepShell>
        )}
      </motion.div>

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="onboarding-back"
          className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium disabled:opacity-40"
        >
          Back
        </button>
        <button
          onClick={() => saveDraft(false)}
          data-testid="onboarding-save"
          className="rounded-full border border-[#165a71]/30 text-[#165a71] px-5 py-2.5 text-sm font-medium hover:bg-[#165a71]/5 transition-colors"
        >
          Save draft
        </button>
        <div className="flex-1" />
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => saveDraft(true)}
            data-testid="onboarding-next"
            className="rounded-full bg-[#165a71] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#11485a] transition-colors inline-flex items-center gap-2"
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={submit}
            data-testid="onboarding-submit"
            className="rounded-full bg-[#071b22] text-white px-6 py-2.5 text-sm font-medium hover:bg-black transition-colors inline-flex items-center gap-2"
          >
            Submit for review <Send className="h-4 w-4" />
          </button>
        )}
      </div>

      <style>{INPUT_CSS}</style>
    </DashboardLayout>
  );
}

const StepShell = ({ title, subtitle, children }) => (
  <div>
    <div className="mb-6">
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22]">{title}</h2>
      {subtitle && <p className="text-sm text-[#252A34]/70 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#071b22]">{label}</span>
    {children}
  </label>
);

const INPUT_CSS = `
.input { width: 100%; border-radius: 0.75rem; border: 1px solid #E2E8F0; background: #fff; padding: 0.75rem 1rem; font-size: 0.95rem; color: #071b22; outline: none; transition: border-color .15s, box-shadow .15s; }
.input:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22,90,113,0.15); }
`;
